import { useState, useEffect, useCallback, useRef } from 'react'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Search, X, Users, Download, MessageCircle, ChevronRight, Check } from 'lucide-react'
import PageHeader from '../../../components/PageHeader'
import PageTip from '../../../components/PageTip'
import GuestRsvpReminder from './GuestRsvpReminder'

const CACHE_KEY = 'fm_guests_cache'
const COMMON_WORDS = ['you','test','guest','name','me','him','her','they','person','someone']

function guestDisplayName(g) { return g.plus_one ? `Mr & Mrs ${g.name}` : g.name }
function peopleCount(list) { return list.reduce((s,g) => s+(g.plus_one?2:1), 0) }
function autoCapitalize(n) { return n.replace(/\b\w/g, c => c.toUpperCase()) }
function validateName(n) {
  const t = n.trim()
  if (t.length < 2) return 'Name must be at least 2 characters'
  if (COMMON_WORDS.includes(t.toLowerCase())) return 'Please enter a real guest name'
  return null
}

function buildWhatsAppMsg(guest, wedding, events) {
  const name   = guestDisplayName(guest)
  const couple = `${wedding.partner1_name} & ${wedding.partner2_name}`
  const link   = `${window.location.origin}/w/${wedding.slug}/invite/${guest.invite_token}`
  const site   = `${window.location.origin}/w/${wedding.slug}`
  const ids    = guest.invited_event_ids
  const guestEvents = (!ids||ids.length===0) ? events : events.filter(e=>ids.includes(e.id))
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : ''
  const eventLines = guestEvents.map(e=>{
    let block = `• ${e.type}${e.date?` on ${formatDate(e.date)}`:''}${e.venue?` at ${e.venue}`:''}`
    const parts = [e.dress_code, e.dress_code_fabric].filter(Boolean)
    if (parts.length || e.dress_code_notes) {
      let dc = `\n   Dress code: ${parts.join(' · ')}`
      if (e.dress_code_notes) dc += parts.length ? ` — ${e.dress_code_notes}` : e.dress_code_notes
      block += dc
    }
    return block
  }).join('\n\n')
  return `Dear ${name},\n\nYou're warmly invited to the wedding of ${couple}\n\n${eventLines}\n\nRSVP via your personal link:\n${link}\n\nWedding details: ${site}\n\nWith love,\n${couple}`
}

function formatWhatsAppPhone(phone) {
  if (!phone) return null
  const digits = phone.replace(/[\s\-+()]/g, '')
  // If starts with 0, assume Nigerian — replace with 234
  if (digits.startsWith('0')) return '234' + digits.slice(1)
  return digits
}

// ── Stat SVG icons ────────────────────────────────────────
const IconPeople = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
const IconCheckCircle = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
const IconClock = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
const IconXCircle = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>

// ── Add Guest Modal ───────────────────────────────────────
function AddGuestModal({ weddingId, events, onClose, onAdded }) {
  const [name, setName]             = useState('')
  const [nameErr, setNameErr]       = useState('')
  const [plusOne, setPlusOne]       = useState(false)
  const [plusOneName, setPlusOneName] = useState('')
  const [selectedEvents, setSelectedEvents] = useState([])
  const [allEvents, setAllEvents]   = useState(true)
  const [phone, setPhone]           = useState('')
  const [saving, setSaving]         = useState(false)
  const [err, setErr]               = useState('')

  function toggleEvent(id) { setSelectedEvents(prev=>prev.includes(id)?prev.filter(e=>e!==id):[...prev,id]) }

  function handleNameChange(val) {
    const capitalized = autoCapitalize(val)
    setName(capitalized)
    if (nameErr) setNameErr(validateName(capitalized) || '')
  }

  async function save() {
    const vErr = validateName(name)
    if (vErr) { setNameErr(vErr); return }
    if (!phone.trim()) { setErr('Please enter a phone number.'); return }
    setSaving(true); setErr('')
    try {
      const { error } = await supabase.from('guests').insert({
        wedding_id: weddingId, name: name.trim(), plus_one: plusOne,
        plus_one_name: plusOneName.trim() || null,
        phone: phone.trim(),
        guest_type: 'general', rsvp_status: 'pending',
        invite_sent: false, whatsapp_sent: false,
        invited_event_ids: allEvents ? [] : selectedEvents,
      })
      if (error) { setErr(error.message); setSaving(false); return }
      await onAdded()
      onClose()
    } catch (e) {
      setErr(e?.message || 'Could not add the guest — please try again.')
      setSaving(false)
    }
  }

  const lbl = { fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'0.3rem' }

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem',backdropFilter:'blur(4px)' }}>
      <div className="card fade-up" style={{ width:'100%',maxWidth:440,padding:'1.5rem',position:'relative',maxHeight:'90vh',overflowY:'auto' }}>
        <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'none',border:'none',cursor:'pointer',color:'#BCBCBC' }}><X size={18}/></button>
        <h3 style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',margin:'0 0 1.25rem' }}>Add Guest</h3>
        <div style={{ display:'flex',flexDirection:'column',gap:'0.75rem' }}>
          <div>
            <label style={lbl}>Full Name *</label>
            <input className="input-field" placeholder="e.g. Funke Alade" value={name} onChange={e=>handleNameChange(e.target.value)} onBlur={()=>setNameErr(validateName(name)||'')} autoFocus/>
            {nameErr && <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#DC2626',margin:'0.25rem 0 0' }}>{nameErr}</p>}
          </div>
          <div><label style={lbl}>Phone *</label><input className="input-field" placeholder="08012345678" value={phone} onChange={e=>setPhone(e.target.value)}/></div>
          <div>
            <label style={lbl}>Invited to</label>
            <div style={{ display:'flex',flexDirection:'column',gap:'0.4rem' }}>
              <label style={{ display:'flex',alignItems:'center',gap:'0.625rem',cursor:'pointer',padding:'0.5rem 0.625rem',background:allEvents?'rgba(155,175,155,0.1)':'#FAFAF8',borderRadius:'0.625rem',border:`1.5px solid ${allEvents?'rgba(155,175,155,0.4)':'#E5E0D8'}` }}>
                <input type="checkbox" checked={allEvents} onChange={e=>{setAllEvents(e.target.checked);if(e.target.checked)setSelectedEvents([])}} style={{ width:15,height:15,accentColor:'#9BAF9B' }}/>
                <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.85rem',fontWeight:600,color:'#1A1A1A' }}>All Events</span>
              </label>
              {events.map(ev=>(
                <label key={ev.id} style={{ display:'flex',alignItems:'center',gap:'0.625rem',cursor:'pointer',padding:'0.5rem 0.625rem',background:selectedEvents.includes(ev.id)?'rgba(196,130,106,0.06)':'#FAFAF8',borderRadius:'0.625rem',border:`1.5px solid ${selectedEvents.includes(ev.id)?'rgba(196,130,106,0.3)':'#E5E0D8'}` }}>
                  <input type="checkbox" checked={selectedEvents.includes(ev.id)} onChange={()=>toggleEvent(ev.id)} style={{ width:15,height:15,accentColor:'#C4826A' }}/>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.85rem',fontWeight:600,color:'#1A1A1A' }}>{ev.type}</span>
                </label>
              ))}
            </div>
          </div>
          <label style={{ display:'flex',alignItems:'flex-start',gap:'0.625rem',cursor:'pointer',padding:'0.75rem',background:'#FAFAF8',borderRadius:'0.75rem',border:'1px solid #E5E0D8' }}>
            <input type="checkbox" checked={plusOne} onChange={e=>setPlusOne(e.target.checked)} style={{ width:15,height:15,accentColor:'#C4826A',flexShrink:0,marginTop:2 }}/>
            <div style={{ flex:1 }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.875rem',margin:'0 0 0.1rem',color:'#1A1A1A' }}>Coming with a +1</p>
              {plusOne && <input className="input-field" placeholder="+1 name (optional)" value={plusOneName} onChange={e=>setPlusOneName(e.target.value)} style={{ marginTop:'0.5rem',fontSize:'0.82rem' }}/>}
            </div>
          </label>
        </div>
        {err&&<p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#C4826A',marginTop:'0.625rem' }}>{err}</p>}
        <div style={{ display:'flex',gap:'0.75rem',marginTop:'1.25rem' }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex:1 }}>Cancel</button>
          <button onClick={save} disabled={saving||(!allEvents&&selectedEvents.length===0)}
            style={{ flex:1,background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:600,fontSize:'0.875rem',opacity:(saving||(!allEvents&&selectedEvents.length===0))?0.6:1 }}>
            {saving?'Adding…':'Add Guest'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Guest Card ────────────────────────────────────────────
function GuestCard({ guest, wedding, events, onDelete, onSendWhatsApp, selected, onSelect, selectionMode }) {
  const [expanded, setExpanded]     = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [plusExpanded, setPlusExpanded] = useState(false)
  const [deleting, setDeleting]     = useState(false)
  const [sending, setSending]       = useState(false)
  const longPressRef = useRef(null)

  const name   = guestDisplayName(guest)
  const status = guest.rsvp_status
  const sc = status==='attending'||status==='confirmed'
    ? { label:'Confirmed', color:'#3D8B5E', bg:'rgba(82,163,105,0.12)' }
    : status==='declined'||status==='rejected'
    ? { label:'Declined',  color:'#B91C1C', bg:'rgba(220,38,38,0.1)' }
    : { label:'Pending',   color:'#B45309', bg:'rgba(217,119,6,0.1)' }

  const ids        = guest.invited_event_ids
  const guestEvents = (!ids||ids.length===0) ? events : events.filter(e=>ids.includes(e.id))

  async function handleSend() { setSending(true); await onSendWhatsApp(guest); setSending(false) }
  async function handleDelete() {
    setDeleting(true)
    await onDelete(guest.id)
    setDeleting(false); setConfirmDel(false); setExpanded(false)
  }

  function onLongPressStart() { longPressRef.current = setTimeout(() => onSelect(guest.id), 500) }
  function onLongPressEnd()   { clearTimeout(longPressRef.current) }

  return (
    <div style={{ borderRadius:'0.875rem', border:`1.5px solid ${selected?'#C4826A':'#E5E0D8'}`, background: selected?'rgba(196,130,106,0.04)':'white', overflow:'visible', transition:'border-color 0.15s' }}>
      {/* Card face */}
      <div style={{ padding:'0.75rem', display:'flex', alignItems:'flex-start', gap:'0.625rem' }}
        onTouchStart={onLongPressStart} onTouchEnd={onLongPressEnd} onTouchCancel={onLongPressEnd}>

        {/* Checkbox (selection mode or hover on desktop) */}
        <div onClick={e=>{e.stopPropagation();onSelect(guest.id)}} style={{ width:34,height:34,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer' }}>
          {selectionMode || selected ? (
            <div style={{ width:20,height:20,borderRadius:5,border:`2px solid ${selected?'#C4826A':'#BCBCBC'}`,background:selected?'#C4826A':'white',display:'flex',alignItems:'center',justifyContent:'center' }}>
              {selected && <Check size={12} color="white"/>}
            </div>
          ) : (
            <div style={{ width:34,height:34,background:'rgba(196,130,106,0.1)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center' }}>
              <span style={{ fontFamily:'Gibs, serif',fontSize:'0.85rem',color:'#C4826A' }}>{guest.name[0].toUpperCase()}</span>
            </div>
          )}
        </div>

        {/* Main content — tapping expands */}
        <div style={{ flex:1,minWidth:0,cursor:'pointer' }} onClick={()=>{ if(!selectionMode) setExpanded(e=>!e) }}>
          <div style={{ display:'flex',alignItems:'center',gap:'0.375rem',flexWrap:'wrap' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',color:'#1A1A1A',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{name}</p>
            {guest.plus_one && (
              <span onClick={e=>{e.stopPropagation();setPlusExpanded(p=>!p)}} style={{ fontSize:'0.6rem',color:'#9BAF9B',fontWeight:700,background:'rgba(155,175,155,0.15)',borderRadius:100,padding:'1px 6px',cursor:'pointer',flexShrink:0 }}>+1</span>
            )}
          </div>
          {/* Plus-one name */}
          {guest.plus_one && plusExpanded && (
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B',margin:'0.15rem 0 0' }}>
              +1 — {guest.plus_one_name || <span style={{ color:'#BCBCBC' }}>Name not provided</span>}
            </p>
          )}
          {/* Phone/email */}
          {(guest.phone||guest.email) && (
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#8C7B70',margin:'0.15rem 0 0',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
              {guest.phone||guest.email}
            </p>
          )}
          {/* Event tags */}
          <div style={{ display:'flex',gap:'0.25rem',flexWrap:'wrap',marginTop:'0.25rem' }}>
            {guestEvents.length===0
              ? <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.6rem',color:'#9BAF9B' }}>All Events</span>
              : guestEvents.slice(0,3).map(e=><span key={e.id} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.6rem',color:'#C4826A',background:'rgba(196,130,106,0.08)',borderRadius:100,padding:'1px 5px' }}>{e.type}</span>)
            }
          </div>
          {/* Dietary */}
          {guest.dietary_notes && (
            <span style={{ display:'inline-flex',alignItems:'center',gap:3,marginTop:'0.25rem',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',color:'#6B7B50',background:'rgba(107,123,80,0.08)',borderRadius:100,padding:'1px 7px' }}>
              🌿 {guest.dietary_notes}
            </span>
          )}
        </div>

        {/* Right side: status + chevron */}
        <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'0.375rem',flexShrink:0 }}>
          <div style={{ display:'inline-flex',alignItems:'center',background:sc.bg,borderRadius:100,padding:'2px 8px' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:sc.color }}>{sc.label}</span>
          </div>
          <div onClick={()=>{ if(!selectionMode) setExpanded(e=>!e) }} style={{ cursor:'pointer',color:'#BCBCBC',transition:'transform 0.2s',transform:expanded?'rotate(90deg)':'none' }}>
            <ChevronRight size={15}/>
          </div>
        </div>
      </div>

      {/* Send/Resend pill — disappears once the guest has confirmed attendance */}
      {!(guest.rsvp_status==='attending'||guest.rsvp_status==='confirmed') && (
      <div style={{ padding:'0 0.75rem 0.625rem',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
        <div/>
        <button onClick={handleSend} disabled={sending}
          style={{ display:'flex',alignItems:'center',gap:'0.3rem',background:'transparent',border:`1.5px solid ${guest.whatsapp_sent?'rgba(155,175,155,0.4)':'rgba(196,130,106,0.5)'}`,borderRadius:100,padding:'0.4rem 0.875rem',cursor:'pointer',minHeight:44,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:guest.whatsapp_sent?'#9BAF9B':'#C4826A' }}>
          <MessageCircle size={11}/>
          {sending?'…':guest.whatsapp_sent?'↺ Resend Invite':'✉ Send Invite'}
        </button>
      </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop:'1px solid #F3F0EA',padding:'0.875rem 0.75rem',display:'flex',flexDirection:'column',gap:'0.5rem' }}>
          {[
            { label:'Phone',    value:guest.phone },
            { label:'Email',    value:guest.email },
            { label:'Status',   value:sc.label },
            { label:'Dietary',  value:guest.dietary_notes },
            { label:'+1 Name',  value:guest.plus_one ? (guest.plus_one_name||'Not provided') : null },
            { label:'Table',    value:guest.table_number },
            { label:'Notes',    value:guest.notes },
          ].filter(r=>r.value).map(r=>(
            <div key={r.label} style={{ display:'flex',gap:'0.5rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#9B9B9B',minWidth:64 }}>{r.label}</span>
              <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#1A1A1A',fontWeight:600 }}>{r.value}</span>
            </div>
          ))}
          {/* Actions */}
          {!confirmDel ? (
            <div style={{ display:'flex',gap:'0.5rem',marginTop:'0.25rem' }}>
              <button onClick={()=>setConfirmDel(true)} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#DC2626',background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:100,padding:'0.5rem',cursor:'pointer' }}>
                {deleting?'Removing…':'Delete Guest'}
              </button>
            </div>
          ) : (
            <div style={{ background:'rgba(220,38,38,0.04)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:'0.75rem',padding:'0.75rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#DC2626',margin:'0 0 0.5rem' }}>Delete {guest.name}? This cannot be undone.</p>
              <div style={{ display:'flex',gap:'0.5rem' }}>
                <button onClick={()=>setConfirmDel(false)} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:600,color:'#6B6B6B',background:'#F3F0EA',border:'none',borderRadius:100,padding:'0.5rem',cursor:'pointer' }}>Cancel</button>
                <button onClick={handleDelete} disabled={deleting} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'white',background:'#DC2626',border:'none',borderRadius:100,padding:'0.5rem',cursor:'pointer' }}>{deleting?'Removing…':'Yes, Delete'}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function GuestsPage() {
  const { wedding, events , isPremium } = useWedding()
  const navigate            = useNavigate()
  const [guests, setGuests]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [search, setSearch]           = useState('')
  const [eventTab, setEventTab]       = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showAdd, setShowAdd]         = useState(false)
  const [selectedGuests, setSelectedGuests] = useState(new Set())
  const [selectionMode, setSelectionMode]   = useState(false)
  const [bulkConfirm, setBulkConfirm]       = useState(null) // 'confirm' | 'delete'

  const fetchGuests = useCallback(async () => {
    if (!wedding?.id) return
    try { const c=localStorage.getItem(`${CACHE_KEY}_${wedding.id}`); if(c){setGuests(JSON.parse(c));setLoading(false)} } catch {}
    const { data } = await supabase.from('guests').select('*').eq('wedding_id',wedding.id).order('created_at',{ascending:false})
    const list=data||[]; setGuests(list); setLoading(false)
    try { localStorage.setItem(`${CACHE_KEY}_${wedding.id}`,JSON.stringify(list)) } catch {}
  },[wedding?.id])

  useEffect(()=>{ fetchGuests() },[fetchGuests])

  // Real-time: reflect guest RSVPs (confirm/decline) the moment they happen
  useEffect(() => {
    if (!wedding?.id) return
    const ch = supabase.channel(`guests-rt-${wedding.id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'guests', filter:`wedding_id=eq.${wedding.id}` }, () => fetchGuests())
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [wedding?.id, fetchGuests])

  async function deleteGuest(id) {
    await supabase.from('guests').delete().eq('id',id)
    const updated=guests.filter(g=>g.id!==id); setGuests(updated)
    try { localStorage.setItem(`${CACHE_KEY}_${wedding.id}`,JSON.stringify(updated)) } catch {}
  }

  async function sendWhatsApp(guest) {
    const msg=buildWhatsAppMsg(guest,wedding,events)
    const phone = formatWhatsAppPhone(guest.phone)
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`
    window.open(url,'_blank')
    const updates={ whatsapp_sent:true, whatsapp_sent_at:new Date().toISOString() }
    await supabase.from('guests').update(updates).eq('id',guest.id)
    const updated=guests.map(g=>g.id===guest.id?{...g,...updates}:g); setGuests(updated)
    try { localStorage.setItem(`${CACHE_KEY}_${wedding.id}`,JSON.stringify(updated)) } catch {}
  }

  function toggleSelect(id) {
    setSelectionMode(true)
    setSelectedGuests(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      if (next.size === 0) setSelectionMode(false)
      return next
    })
  }

  function cancelSelection() { setSelectedGuests(new Set()); setSelectionMode(false) }

  async function bulkMarkConfirmed() {
    const ids = [...selectedGuests]
    await Promise.all(ids.map(id => supabase.from('guests').update({ rsvp_status:'confirmed' }).eq('id',id)))
    await fetchGuests(); cancelSelection(); setBulkConfirm(null)
  }

  async function bulkDelete() {
    const ids = [...selectedGuests]
    await Promise.all(ids.map(id => supabase.from('guests').delete().eq('id',id)))
    await fetchGuests(); cancelSelection(); setBulkConfirm(null)
  }

  function exportCSV() {
    const rows=[['Name','Phone','Email','Events','Plus One','Status','Dietary']]
    filtered.forEach(g=>{
      const ids=g.invited_event_ids; const evNames=(!ids||ids.length===0)?'All Events':events.filter(e=>ids.includes(e.id)).map(e=>e.type).join('; ')
      const st=g.rsvp_status==='attending'||g.rsvp_status==='confirmed'?'Confirmed':g.rsvp_status==='declined'||g.rsvp_status==='rejected'?'Declined':'Pending'
      rows.push([guestDisplayName(g),g.phone||'',g.email||'',evNames,g.plus_one?'Yes':'No',st,g.dietary_notes||''])
    })
    const csv=rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8;'}); const url=URL.createObjectURL(blob)
    const a=document.createElement('a'); a.href=url; a.download='guests.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const byEvent = guests.filter(g => {
    if (eventTab==='all') return true
    const ids=g.invited_event_ids; if(!ids||ids.length===0) return true
    return ids.includes(eventTab)
  })
  const filtered = byEvent.filter(g => {
    const ms=!search||g.name.toLowerCase().includes(search.toLowerCase())
    const mf=statusFilter==='all'?true
      :statusFilter==='confirmed'?(g.rsvp_status==='attending'||g.rsvp_status==='confirmed')
      :statusFilter==='declined'?(g.rsvp_status==='declined'||g.rsvp_status==='rejected')
      :g.rsvp_status==='pending'
    return ms&&mf
  })
  const tabGuests  = eventTab==='all' ? guests : guests.filter(g=>{ const ids=g.invited_event_ids; return !ids||ids.length===0||ids.includes(eventTab) })
  const total      = peopleCount(tabGuests)
  const confirmed  = tabGuests.filter(g=>g.rsvp_status==='attending'||g.rsvp_status==='confirmed')
  const pending    = tabGuests.filter(g=>g.rsvp_status==='pending')
  const declined   = tabGuests.filter(g=>g.rsvp_status==='declined'||g.rsvp_status==='rejected')
  const plusOnes   = tabGuests.filter(g=>g.plus_one).length
  const responded  = confirmed.length + declined.length
  const responseRate = tabGuests.length > 0 ? Math.round((responded/tabGuests.length)*100) : 0
  const FREE_GUEST_LIMIT = 5
  const atGuestLimit = !isPremium && guests.length >= FREE_GUEST_LIMIT

  if (!wedding) return (
    <div style={{ textAlign:'center', padding:'3rem' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>One moment…</p>
    </div>
  )

  const STAT_CARDS = [
    { label:'Total Guests', value:total, sub:plusOnes>0?`Includes ${plusOnes} plus-ones`:null, icon:<IconPeople/>, numColor:'#1A1A1A', bg:'#FEFCFA', border:'1px solid #E8E0D8' },
    { label:'Confirmed',    value:peopleCount(confirmed), icon:<IconCheckCircle/>, numColor:'#3D8B5E', bg:'rgba(82,163,105,0.08)', border:'1px solid rgba(82,163,105,0.2)' },
    { label:'Pending',      value:peopleCount(pending),   icon:<IconClock/>,       numColor:'#B45309', bg:'rgba(217,119,6,0.07)',  border:'1px solid rgba(217,119,6,0.2)' },
    { label:'Declined',     value:peopleCount(declined),  icon:<IconXCircle/>,     numColor:'#B91C1C', bg:'rgba(220,38,38,0.07)',  border:'1px solid rgba(220,38,38,0.2)' },
  ]

  return (
    <div style={{ display:'flex',flexDirection:'column',gap:'1.25rem',minWidth:0,width:'100%',overflow:'hidden' }}>

      {/* Header */}
      <PageHeader
        title="Guests & RSVP"
        subtitle={`Everyone you love, in one place${total ? ` · ${total} invited, ${peopleCount(confirmed)} can't wait to celebrate you` : ''}.`}
        right={
          <div style={{ display:'flex',gap:'0.5rem',flexShrink:0 }}>
            {isPremium&&<button onClick={exportCSV} className="btn-secondary" style={{ fontSize:'0.78rem',padding:'0.5rem 0.75rem',display:'flex',alignItems:'center',gap:'0.3rem' }}><Download size={13}/> CSV</button>}
            <button onClick={()=>{ if(atGuestLimit){ alert(`Free plan limit: ${FREE_GUEST_LIMIT} guests. Upgrade for unlimited.`); return } setShowAdd(true) }}
              style={{ display:'flex',alignItems:'center',gap:'0.35rem',background:'#C4826A',color:'white',border:'none',borderRadius:100,padding:'0.6rem 1rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',whiteSpace:'nowrap' }}>
              <UserPlus size={14}/> Add Guest
            </button>
          </div>
        }
      />

      <PageTip id="guests" title="Invite guests, your way"
        points={[
          'Add a guest, then send their personal invite on WhatsApp.',
          "Each link is unique, so it can't be forwarded to gate-crashers.",
          'Track who is coming, per event.',
        ]}/>

      {/* Free limit */}
      {!isPremium && (
        <div style={{ background:'rgba(196,130,106,0.06)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'0.75rem',padding:'0.625rem 1rem',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.75rem',flexWrap:'wrap' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:atGuestLimit?'#C4826A':'#6B6B6B',margin:0,fontWeight:atGuestLimit?700:400 }}>
            {guests.length}/{FREE_GUEST_LIMIT} guests · free plan{atGuestLimit?' (limit reached)':''}
          </p>
          {atGuestLimit&&<button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ fontSize:'0.72rem',padding:'0.3rem 0.75rem' }}>Upgrade</button>}
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'0.5rem' }}>
        {STAT_CARDS.map(s=>(
          <div key={s.label} style={{ background:s.bg,border:s.border,borderRadius:'0.875rem',padding:'0.875rem 0.75rem' }}>
            <div style={{ width:32,height:32,background:'rgba(255,255,255,0.6)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'0.5rem',color:s.numColor }}>
              {s.icon}
            </div>
            <p style={{ fontFamily:'Gibs, serif',fontSize:'1.6rem',color:s.numColor,margin:'0 0 0.1rem',fontWeight:400,lineHeight:1 }}>{s.value}</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#6B6B6B',margin:0,fontWeight:600 }}>{s.label}</p>
            {s.sub && <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',color:'#9B9B9B',margin:'0.15rem 0 0' }}>{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* RSVP Progress bar */}
      {tabGuests.length > 0 && (
        <div style={{ background:'white',border:'1px solid #E5E0D8',borderRadius:'0.875rem',padding:'0.875rem 1rem' }}>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.5rem' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#1A1A1A' }}>RSVP Progress</span>
            <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#6B6B6B' }}>{responded} of {tabGuests.length} responded</span>
          </div>
          <div style={{ height:6,background:'#F0EBE3',borderRadius:100,overflow:'hidden',marginBottom:'0.5rem' }}>
            <div style={{ height:'100%',width:`${responseRate}%`,background:'#B36B55',borderRadius:100,transition:'width 0.6s' }}/>
          </div>
          {pending.length > 0 ? (
            <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#9B9B9B' }}>{pending.length} guest{pending.length!==1?'s':''} yet to respond</span>
          ) : (
            <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#3D8B5E',fontWeight:600,margin:0 }}>All guests have responded ✓</p>
          )}
          {/* Remind everyone who hasn't responded — one WhatsApp at a time */}
          <div style={{ marginTop:'0.75rem' }}>
            <GuestRsvpReminder pending={pending} wedding={wedding}/>
          </div>
        </div>
      )}

      {/* Auto-confirm tip */}
      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'-0.5rem 0 0', lineHeight:1.5 }}>
        Guests are automatically confirmed when they accept their invite, and declined when they can't make it.
      </p>

      {/* Event tabs */}
      {events.length > 1 && (
        <div style={{ display:'flex',overflowX:'auto',scrollbarWidth:'none',WebkitOverflowScrolling:'touch',borderBottom:'1px solid #E5E0D8' }}>
          {[{id:'all',label:`All (${guests.length})`},...events.map(e=>({id:e.id,label:`${e.type} (${guests.filter(g=>!g.invited_event_ids||g.invited_event_ids.length===0||g.invited_event_ids.includes(e.id)).length})`}))].map(t=>(
            <button key={t.id} onClick={()=>setEventTab(t.id)} style={{ padding:'0.5rem 0.875rem',background:'none',border:'none',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',fontWeight:eventTab===t.id?700:500,color:eventTab===t.id?'#C4826A':'#6B6B6B',borderBottom:eventTab===t.id?'2px solid #C4826A':'2px solid transparent',whiteSpace:'nowrap',flexShrink:0,minHeight:40,transition:'all 0.15s' }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ position:'relative' }}>
        <Search size={13} color="#BCBCBC" style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)' }}/>
        <input className="input-field" placeholder="Search guests…" value={search} onChange={e=>setSearch(e.target.value)} style={{ paddingLeft:'2.25rem' }}/>
      </div>

      {/* Status filter */}
      <div style={{ display:'flex',background:'#F3F0EA',borderRadius:100,padding:'2px',gap:'1px',width:'100%' }}>
        {[{v:'all',l:'All'},{v:'confirmed',l:'Confirmed'},{v:'pending',l:'Pending'},{v:'declined',l:'Declined'}].map(t=>(
          <button key={t.v} onClick={()=>setStatusFilter(t.v)} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.68rem',fontWeight:statusFilter===t.v?700:500,padding:'0.35rem 0.15rem',borderRadius:100,border:'none',cursor:'pointer',background:statusFilter===t.v?'white':'transparent',color:statusFilter===t.v?'#1A1A1A':'#6B6B6B',transition:'all 0.15s',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',textAlign:'center',minWidth:0 }}>
            {t.l}
          </button>
        ))}
      </div>

      {/* Selection bar */}
      {selectionMode && (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem',flexWrap:'wrap' }}>
          <button onClick={()=>{ setSelectedGuests(new Set(filtered.map(g=>g.id))); setSelectionMode(true) }} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#C4826A',background:'none',border:'none',cursor:'pointer',padding:0 }}>
            ☑ Select All ({filtered.length})
          </button>
          <button onClick={cancelSelection} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:600,color:'#6B6B6B',background:'none',border:'none',cursor:'pointer',padding:0 }}>Cancel</button>
        </div>
      )}

      {/* List */}
      {loading&&guests.length===0 ? (
        <p style={{ fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC',textAlign:'center',padding:'2rem' }}>Just a second…</p>
      ) : filtered.length===0 ? (
        <div style={{ textAlign:'center',padding:'3rem 1rem' }}>
          {guests.length===0 ? (
            <div style={{ width:120, height:120, margin:'0 auto 1.25rem', borderRadius:'1.25rem', overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.08)', border:'3px solid white' }}>
              <img src="/brand/silhouette-heart-700.webp" alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            </div>
          ) : (
            <Users size={36} color="#E5E0D8" style={{ marginBottom:'0.875rem' }}/>
          )}
          <p style={{ fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',color:'#1A1A1A',marginBottom:'0.35rem' }}>{guests.length===0?'Time to start the list':'No matches found'}</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.8rem',color:'#6B6B6B',lineHeight:1.65 }}>{guests.length===0?"Add your first guest and send them a personal invite.":'Try a different search.'}</p>
        </div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:'0.5rem' }}>
          {filtered.map(guest=>(
            <GuestCard key={guest.id} guest={guest} wedding={wedding} events={events}
              onDelete={deleteGuest} onSendWhatsApp={sendWhatsApp}
              selected={selectedGuests.has(guest.id)} onSelect={toggleSelect} selectionMode={selectionMode}/>
          ))}
        </div>
      )}

      {showAdd&&<AddGuestModal weddingId={wedding.id} events={events} onClose={()=>setShowAdd(false)} onAdded={fetchGuests}/>}

      {/* Bulk action bar */}
      {selectedGuests.size > 0 && (
        <div style={{ position:'fixed',bottom:0,left:0,right:0,zIndex:80,background:'white',borderTop:'1px solid #E5E0D8',padding:'0.875rem 1rem',boxShadow:'0 -4px 20px rgba(0,0,0,0.08)',animation:'slideUp 0.25s ease-out' }}>
          <style>{`@keyframes slideUp { from { transform:translateY(100%); opacity:0 } to { transform:translateY(0); opacity:1 } }`}</style>
          {bulkConfirm === 'confirm' ? (
            <div style={{ display:'flex',flexDirection:'column',gap:'0.625rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:700,color:'#1A1A1A',margin:0 }}>Mark {selectedGuests.size} guest{selectedGuests.size!==1?'s':''} as confirmed?</p>
              <div style={{ display:'flex',gap:'0.5rem' }}>
                <button onClick={()=>setBulkConfirm(null)} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#6B6B6B',background:'#F3F0EA',border:'none',borderRadius:100,padding:'0.6rem',cursor:'pointer' }}>Cancel</button>
                <button onClick={bulkMarkConfirmed} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'white',background:'#3D8B5E',border:'none',borderRadius:100,padding:'0.6rem',cursor:'pointer' }}>Yes, Confirm</button>
              </div>
            </div>
          ) : bulkConfirm === 'delete' ? (
            <div style={{ display:'flex',flexDirection:'column',gap:'0.625rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:700,color:'#DC2626',margin:0 }}>Delete {selectedGuests.size} guest{selectedGuests.size!==1?'s':''}? This cannot be undone.</p>
              <div style={{ display:'flex',gap:'0.5rem' }}>
                <button onClick={()=>setBulkConfirm(null)} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:600,color:'#6B6B6B',background:'#F3F0EA',border:'none',borderRadius:100,padding:'0.6rem',cursor:'pointer' }}>Cancel</button>
                <button onClick={bulkDelete} style={{ flex:1,fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'white',background:'#DC2626',border:'none',borderRadius:100,padding:'0.6rem',cursor:'pointer' }}>Yes, Delete</button>
              </div>
            </div>
          ) : (
            <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.5rem',flexWrap:'wrap' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:700,color:'#1A1A1A' }}>{selectedGuests.size} selected</span>
              <div style={{ display:'flex',gap:'0.5rem',flexWrap:'wrap' }}>
                <button onClick={()=>{ const msgs=filtered.filter(g=>selectedGuests.has(g.id)).map(g=>buildWhatsAppMsg(g,wedding,events)).join('\n---\n'); window.open(`https://wa.me/?text=${encodeURIComponent(msgs)}`,'_blank') }} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.1)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:100,padding:'0.45rem 0.875rem',cursor:'pointer' }}>Send Invites</button>
                <button onClick={()=>setBulkConfirm('confirm')} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#3D8B5E',background:'rgba(82,163,105,0.1)',border:'1px solid rgba(82,163,105,0.2)',borderRadius:100,padding:'0.45rem 0.875rem',cursor:'pointer' }}>Mark Confirmed</button>
                <button onClick={()=>setBulkConfirm('delete')} style={{ fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#DC2626',background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:100,padding:'0.45rem 0.875rem',cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
